/**
 * UI store - global UI state management
 * Non-persisted state for temporary UI interactions
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface UIState {
  // Sidebar state
  sidebarOpen: boolean;
  chatSidebarOpen: boolean;
  
  // Modal states
  activeModal: string | null;
  modalData: any;
  
  // Loading states
  globalLoading: boolean;
  loadingMessage: string | null;
  
  // Toast/notification queue
  toasts: Array<{
    id: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    duration?: number;
  }>;
  
  // Actions
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  
  setChatSidebarOpen: (open: boolean) => void;
  toggleChatSidebar: () => void;
  
  openModal: (modalId: string, data?: any) => void;
  closeModal: () => void;
  
  setGlobalLoading: (loading: boolean, message?: string) => void;
  
  addToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    (set) => ({
      // Initial state
      sidebarOpen: false,
      chatSidebarOpen: false,
      activeModal: null,
      modalData: null,
      globalLoading: false,
      loadingMessage: null,
      toasts: [],

      // Sidebar actions
      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }));
      },

      // Chat sidebar actions
      setChatSidebarOpen: (open: boolean) => {
        set({ chatSidebarOpen: open });
      },

      toggleChatSidebar: () => {
        set((state) => ({ chatSidebarOpen: !state.chatSidebarOpen }));
      },

      // Modal actions
      openModal: (modalId: string, data?: any) => {
        set({ activeModal: modalId, modalData: data });
      },

      closeModal: () => {
        set({ activeModal: null, modalData: null });
      },

      // Loading actions
      setGlobalLoading: (loading: boolean, message?: string) => {
        set({ globalLoading: loading, loadingMessage: message || null });
      },

      // Toast actions
      addToast: (message: string, type = 'info' as const, duration = 5000) => {
        const id = `toast-${Date.now()}-${Math.random()}`;
        
        set((state) => ({
          toasts: [...state.toasts, { id, message, type, duration }],
        }));

        // Auto-remove after duration
        if (duration > 0) {
          setTimeout(() => {
            set((state) => ({
              toasts: state.toasts.filter((t) => t.id !== id),
            }));
          }, duration);
        }
      },

      removeToast: (id: string) => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      },

      clearToasts: () => {
        set({ toasts: [] });
      },
    }),
    { name: 'UIStore' }
  )
);

